import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falComment } from 'shared/@generated/icons/falComment';
import { fasCheck } from 'shared/@generated/icons/fasCheck';
import { fasPartyHorn } from 'shared/@generated/icons/fasPartyHorn';
import { memo, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useRoute } from 'shared/library/routes/route';
import { ActivityIndicator, FlatList, Platform, StyleSheet, View } from 'react-native';
import { useScrollToTop } from 'shared/library/use-scroll-to-top';
import { useSafeAreaInsets } from 'shared/library/use-safe-area-insets';
import { Props } from 'shared/@types';
import { useTranslation } from 'react-i18next';
import { Text } from 'shared/components/text';
import { H1, H2 } from 'shared/components/title';
import { Button } from 'shared/components/button';
import { Icon } from 'shared/components/icon';
import { Payment } from 'shared/components/illustrations/payment';
import { Pickup } from 'shared/components/illustrations/pickup';
import { brevo } from 'shared/library/brevo';
import { staticImage } from 'shared/library/static-images';
import { Image } from 'shared/components/image';
import { Inbox } from 'shared/components/illustrations/inbox';
import { ReviewsSegment } from 'shared/components/reviews-segment';
import { PublishListing } from 'shared/components/illustrations/publish-listing';
import { ReviewsSummaryCard } from 'shared/components/reviews-summary-card';
import { HowItWorksSection, type HowItWorksItem } from 'shared/components/how-it-works-section';
import colors from 'shared/library/colors';
import { useNativeRouteChrome } from 'shared/library/native-route-chrome';
import { useNativeTabBarContentInset } from 'shared/library/use-native-tab-bar-content-inset';
import { AdBanner } from 'shared/components/ad-banner';

interface SellingAPetScreenProps extends Props {
  loadingInitial?: boolean;
}

type NativeSellingAPetSectionKey = 'examples' | 'reviews' | 'loading';

function runWhenIdle(callback: () => void) {
  if (typeof requestIdleCallback === 'function') {
    const requestId = requestIdleCallback(() => {
      callback();
    });

    return {
      cancel() {
        if (typeof cancelIdleCallback === 'function') {
          cancelIdleCallback(requestId);
        }
      },
    };
  }

  const timeoutId = setTimeout(callback, 0);

  return {
    cancel() {
      clearTimeout(timeoutId);
    },
  };
}

const SellingAPetHeroSection = memo(function SellingAPetHeroSection({
  route,
  t,
}: {
  route: ReturnType<typeof useRoute>;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  return (
    <View className={'gap-3 md:flex-row md:items-center md:gap-6 lg:gap-20'}>
      <View className={'md:w-5/12'}>
        <Image
          src={staticImage('mom-with-puppies')}
          alt={t('selling_a_pet.image_alt')}
          containerClassName={'aspect-4/3 w-full'}
          imageClassName={'rounded-lg object-cover'}
          priority={true}
          sizeType={'large'}
        />

        <View className={'absolute bottom-0 right-0 p-3'}>
          <ReviewsSummaryCard className={'bg-white'} />
        </View>
      </View>

      <View className={'w-full md:flex-1'}>
        <View
          className={
            'mb-3 flex-row items-center gap-1.5 self-start rounded-full border border-yellow-950/15 bg-yellow-400 px-3 py-1.5 md:mb-6'
          }
        >
          <Icon icon={fasPartyHorn} className={'mr-1 size-3 text-yellow-950 md:size-4'} />

          <Text className={'text-xs font-semibold text-yellow-950 md:text-sm'}>
            {t('selling_a_pet.sold_over_100k')}
          </Text>
        </View>

        <H1
          title={t('selling_a_pet.title')}
          hero={true}
          className={'max-w-xl'}
          marginBottom={false}
        />

        <Text
          className={'text-brand-brown-dark/80 mt-5 max-w-xl text-lg leading-relaxed md:text-xl'}
        >
          {t('selling_a_pet.description')}
        </Text>

        <View className={'mt-3 w-full gap-1.5 md:mt-6 md:flex-row md:gap-3'}>
          <Button
            text={t('selling_a_pet.publish_listing')}
            icon={falAngleRight}
            color={'brown'}
            href={route('listings.create')}
          />
          <Button
            text={t('common:help')}
            icon={falComment}
            iconPosition={'left'}
            color={'white'}
            onPress={() => brevo.openChat()}
          />
        </View>
      </View>
    </View>
  );
});

const SellingAPetExamplesSection = memo(function SellingAPetExamplesSection({
  t,
  fullBleed = false,
}: {
  t: ReturnType<typeof useTranslation>['t'];
  fullBleed?: boolean;
}) {
  const route = useRoute();
  const assurances = [
    t('selling_a_pet.examples.verified_buyers'),
    t('selling_a_pet.examples.secure_payment'),
    t('selling_a_pet.examples.smooth_handover'),
  ];

  const renderAssuranceItems = () => (
    <View className={'flex-row flex-wrap justify-center gap-3 md:gap-6'}>
      {assurances.map((item) => (
        <View key={item} className={'flex-row items-center gap-2'}>
          <View
            className={'border-brand-green size-5 items-center justify-center rounded-full border'}
          >
            <Icon icon={fasCheck} className={'text-brand-green size-2.5'} />
          </View>
          <Text className={'text-brand-brown-dark/80'}>{item}</Text>
        </View>
      ))}
    </View>
  );

  const content = (
    <View>
      <H2
        title={t('selling_a_pet.examples.title')}
        subtitle={t('selling_a_pet.examples.description')}
        center={true}
        marginTop={false}
        className={'text-brand-brown-dark md:text-3xl'}
      />

      <View className={'mt-3 items-center md:mt-4'}>{renderAssuranceItems()}</View>

      <View className={'mt-10 gap-10 md:mt-12 md:flex-row md:gap-12'}>
        <View className={'md:flex-1'}>
          <Image
            src={staticImage('beagle-litter')}
            alt={t('selling_a_pet.puppies.image_alt')}
            containerClassName={'bg-brand-cream-light aspect-4/3 w-full overflow-hidden rounded-lg'}
            imageClassName={'object-cover'}
            sizeType={'large'}
          />

          <View className={'mt-5'}>
            <Text className={'text-brand-brown-dark text-2xl font-black md:text-3xl'}>
              {t('selling_a_pet.puppies.description')}
            </Text>
            <Text className={'text-brand-brown-dark/80 mt-3 md:text-lg'}>
              {t('selling_a_pet.puppies.body')}
            </Text>
            <View className={'mt-5 self-start'}>
              <Button
                text={t('selling_a_pet.puppies.cta')}
                icon={falAngleRight}
                color={'brown'}
                href={route('selling-a-dog')}
              />
            </View>
          </View>
        </View>

        <View className={'md:flex-1'}>
          <Image
            src={staticImage('kitten-litter')}
            alt={t('selling_a_pet.kittens.image_alt')}
            containerClassName={'bg-brand-cream-light aspect-4/3 w-full overflow-hidden rounded-lg'}
            imageClassName={'object-cover'}
            sizeType={'large'}
          />

          <View className={'mt-5'}>
            <Text className={'text-brand-brown-dark text-2xl font-black md:text-3xl'}>
              {t('selling_a_pet.kittens.description')}
            </Text>
            <Text className={'text-brand-brown-dark/80 mt-3 md:text-lg'}>
              {t('selling_a_pet.kittens.body')}
            </Text>
            <View className={'mt-5 self-start'}>
              <Button
                text={t('selling_a_pet.kittens.cta')}
                icon={falAngleRight}
                color={'brown'}
                href={route('selling-a-cat')}
              />
            </View>
          </View>
        </View>
      </View>
    </View>
  );

  if (!fullBleed) {
    return content;
  }

  return (
    <View
      className={
        'bg-brand-cream-light relative left-1/2 right-1/2 -mx-[50vw] w-screen p-3 md:p-6 md:py-20'
      }
    >
      <View className={'mx-auto w-full max-w-screen-xl'}>{content}</View>
    </View>
  );
});

const NativeSellingAPetScreen = memo(function NativeSellingAPetScreen({
  route,
  t,
  features,
  loadingDeferred,
}: {
  route: ReturnType<typeof useRoute>;
  t: ReturnType<typeof useTranslation>['t'];
  features: HowItWorksItem[];
  loadingDeferred: boolean;
}) {
  const insets = useSafeAreaInsets();
  const { hasNativeHeader } = useNativeRouteChrome();
  const nativeTabBarContentInset = useNativeTabBarContentInset(true);
  const listRef = useRef<FlatList<NativeSellingAPetSectionKey>>(null);
  const sections: NativeSellingAPetSectionKey[] = loadingDeferred
    ? ['loading']
    : ['examples', 'reviews'];

  useScrollToTop(listRef);

  const header = (
    <View className={'px-3'}>
      <View className={'mx-auto w-full max-w-screen-xl'}>
        <SellingAPetHeroSection route={route} t={t} />
        <View className={'h-6'} />
        <HowItWorksSection
          id={'how-it-works'}
          title={t('selling_a_pet.how_it_works')}
          subtitle={t('selling_a_pet.how_it_works_description')}
          items={features}
        />
      </View>
    </View>
  );

  const renderSection = (item: NativeSellingAPetSectionKey): ReactNode => {
    if (item === 'examples') {
      return <SellingAPetExamplesSection t={t} />;
    }

    if (item === 'reviews') {
      return <ReviewsSegment role={'seller'} />;
    }

    return (
      <View className={'items-center justify-center py-12'}>
        <ActivityIndicator size={'large'} color={'#2b120d'} />
      </View>
    );
  };

  return (
    <View className={'bg-brand-cream-light flex-1'} style={nativeStyles.screen}>
      <FlatList
        ref={listRef}
        data={sections}
        keyExtractor={(item) => item}
        style={{ flex: 1 }}
        contentInsetAdjustmentBehavior={'never'}
        automaticallyAdjustContentInsets={false}
        automaticallyAdjustsScrollIndicatorInsets={false}
        contentContainerStyle={{
          paddingTop: hasNativeHeader ? 12 : insets.top + 12,
          paddingBottom: nativeTabBarContentInset,
        }}
        scrollIndicatorInsets={{ bottom: nativeTabBarContentInset }}
        showsVerticalScrollIndicator={false}
        removeClippedSubviews={true}
        initialNumToRender={2}
        maxToRenderPerBatch={2}
        windowSize={4}
        ListHeaderComponent={header}
        ItemSeparatorComponent={() => <View className={'h-6'} />}
        renderItem={({ item }) => (
          <View className={'px-3'}>
            <View className={'mx-auto w-full max-w-screen-xl'}>{renderSection(item)}</View>
          </View>
        )}
      />
    </View>
  );
});

const nativeStyles = StyleSheet.create({
  screen: {
    backgroundColor: colors.brand['cream-light'],
    flex: 1,
  },
});

const SellingAPetScreen = ({ loadingInitial = false }: SellingAPetScreenProps) => {
  const { t } = useTranslation('screens');
  const route = useRoute();
  const [showDeferredContent, setShowDeferredContent] = useState(Platform.OS === 'web');

  useEffect(() => {
    if (loadingInitial || showDeferredContent || Platform.OS === 'web') {
      return undefined;
    }

    const task = runWhenIdle(() => {
      setShowDeferredContent(true);
    });

    return () => {
      task.cancel();
    };
  }, [loadingInitial, showDeferredContent]);

  const features = useMemo<HowItWorksItem[]>(
    () => [
      {
        title: t('selling_a_pet.create_listing'),
        description: t('selling_a_pet.create_listing_description'),
        image: <PublishListing />,
      },
      {
        title: t('selling_a_pet.connect_with_buyer'),
        description: t('selling_a_pet.connect_with_buyer_description'),
        image: <Inbox />,
      },
      {
        title: t('selling_a_pet.transaction_via_petpay'),
        description: t('selling_a_pet.transaction_via_petpay_description'),
        image: <Payment />,
      },
      {
        title: t('selling_a_pet.drop_off'),
        description: t('selling_a_pet.drop_off_description'),
        image: <Pickup />,
      },
    ],
    [t],
  );

  if (loadingInitial) {
    return (
      <View className={'items-center justify-center px-3 py-20'}>
        <ActivityIndicator size={'large'} color={'#2b120d'} />
      </View>
    );
  }

  if (Platform.OS !== 'web') {
    return (
      <NativeSellingAPetScreen
        route={route}
        t={t}
        features={features}
        loadingDeferred={!showDeferredContent}
      />
    );
  }

  return (
    <View className={'p-3 md:p-6 md:py-20'}>
      <View className={'mx-auto w-full max-w-screen-xl'}>
        <SellingAPetHeroSection route={route} t={t} />

        <AdBanner className={'mt-6 md:mt-12'} placement={'top'} />

        <HowItWorksSection
          id={'how-it-works'}
          title={t('selling_a_pet.how_it_works')}
          subtitle={t('selling_a_pet.how_it_works_description')}
          items={features}
        />

        <View className={'mt-6 md:my-12'} />

        {showDeferredContent ? (
          <>
            <SellingAPetExamplesSection t={t} fullBleed={true} />
            <ReviewsSegment role={'seller'} />
          </>
        ) : (
          <View className={'items-center justify-center py-12'}>
            <ActivityIndicator size={'large'} color={'#2b120d'} />
          </View>
        )}
      </View>
    </View>
  );
};

export { SellingAPetScreen };
export type { SellingAPetScreenProps };
